<template>
  <div class="branded-images-list text-center">
    <p class="data-description">
      add one of these <strong>images</strong> from your <strong>brands</strong>
      as <strong>mms</strong> and send your campaign with <strong>free credits</strong>
    </p>
    <a class="brand-clients-link"
      href="/brands_marketplace"
      target="_blank"
      rel="noopener noreferrer">
      don't see a brand you like?
    </a>
    <span v-if="adsError" class="error-label"> {{adsError}} </span>
    <loading-icon v-else-if="loading"></loading-icon>
    <div v-else id="branded-image-container" :class="['branded-image-section', { ['has-overlay']: overlayType }]">
      <div v-for="(ad, index) in adsList" :key="index" class="ads-container-wrapper">
        <ad
          :ad="ad"
          :attachedAdId="attachedAdId"
          :requiresLowerCount="requiresLowerCount(ad)"
          @selectedAd="selectedAd"
          @removeAd="removeAd"
        />
      </div>
      <ads-list-overlay
        :isEmailType="isEmailType"
        :overlayType="overlayType"
        :smsSend="smsSend"
        @removeAd="removeAd"
      />
    </div>
    <image-preview-modal
      v-if="showPreviewModal"
      alt="ad image"
      :disclaimer="currentMerchant.disclaimer"
      :isLink="sendAsLink"
      expandMMS
      :imageClasses="imageRounding"
      :imageSrc="previewUrl"
      :suggestedText="suggestedTextDisplay"
      :requestedFilters="requestedFilters"
      :modalConfig="modalConfig"
      @close="closePreviewModal"
      @upload="uploadImage"
    />
    <modal
        v-if="showAlertModal"
        :wrapperClasses="modalConfig.wrapperClasses"
        @action="showAlertModal = false"
        @close="showAlertModal = false">
      <h3 slot="header" class="title">{{modalConfig.title}}</h3>
      <div slot="body" class="body">
        <p>{{modalConfig.content}}</p>
      </div>
      <div
          slot="action"
          class="button larger modal-click-action"
          @click="showAlertModal = false">
        {{modalConfig.actionButton}}
      </div>
    </modal>
  </div>
</template>

<script>
  import Ad from './Ad'
  import AdsListOverlay from './AdsListOverlay'
  import AxiosMixin from '../../mixins/AxiosMixin'
  import ImagePreviewModal from '../shared/ImagePreviewModal'
  import LoadingIcon from '../shared/LoadingIcon'
  import Modal from '../shared/Modal'
  import { removeTimeZoneAndGetDateUtil } from '../../utilities'
  import { mapState } from 'vuex'
  import { merchantsAPI } from '../../services/api'
  import { isWithinInterval, format } from 'date-fns'

  export default {
    props: {
      adAsLink: Boolean,
      attachedAdId: Number,
      campaignId: String,
      campaignSendTime: String,
      isEmailType: Boolean,
      recipientCount: Number,
      sendAtLocal: String,
      sendNow: Boolean,
      smsSend: Boolean,
      smsSent: Number
    },

    name: 'AdsList',

    components: {
      Ad,
      AdsListOverlay,
      LoadingIcon,
      Modal,
      ImagePreviewModal
    },

    mixins: [AxiosMixin],

    data() {
      return {
        adsError: null,
        adsList: null,
        loading: true,
        modalConfig: null,
        previewAdId: null,
        previewUrl: null,
        sendAsLink: false,
        showAlertModal: false,
        showPreviewModal: false,
        skipAvailabilityCheck: false,
        suggestedText: null,
        previousAttachedAdId: null,
        attachedAd: null,
        requestedFilters: [],
        totalTextableMembers: null
      }
    },

    methods: {
      attachAd() {
        const smsSent = this.requestedFilters?.length > 0 ? this.totalTextableMembers : this.recipientCount

        let dataToUpdate = {
          ad_id: this.previewAdId,
          sms_sent: smsSent,
          image_type: this.sendAsLink ? 'link' : 'mms'
        }

        this.previousAttachedAdId = this.attachedAdId

        this.$emit('updateCampaign', dataToUpdate, null, null, this.suggestedText, true)
        this.previewAdId = null

        this.$emit('selectedAd', this.attachedAd)
      },

      checkAdAvailability(isForAttaching) {
        const id = isForAttaching ? this.previewAdId : this.attachedAdId,
            currentAd = this.adsList.filter(ad => ad.id === id)[0],
            isAvailable = !!currentAd && (this.requestedFilters?.length > 0 || !this.requiresLowerCount(currentAd)),
            availableDates = isAvailable ? { start: currentAd.send_at_start, end: currentAd.send_at_end } : {}

        if (isForAttaching) {
          if (isAvailable) {
            this.attachAd()
          } else {
            this.setUnavailableAlert()
            this.previewAdId = null
          }
          this.$emit('setAvailableDates', availableDates)
        } else {
          if (isAvailable) {
            this.sortCurrentAdToTop()
            this.updateSmsSent()
            this.checkScheduledCampaign(availableDates)
          } else {
            const title = 'campaign settings changed',
                content = 'your brand image no longer fits the selected settings and has been removed'
            this.setAlertModal(title, content)
            this.removeAd()
            this.$emit('setAvailableDates', availableDates)
          }
        }
      },

      checkScheduledCampaign(availableDates) {
        const target = this.sendNow ? format(new Date(), "y-M-d HH:mm") : this.sendAtLocal,
              inAdRange = isWithinInterval(new Date(target),
              {
                start: removeTimeZoneAndGetDateUtil(availableDates.start),
                end: removeTimeZoneAndGetDateUtil(availableDates.end)
              })

        if (!inAdRange) {
          this.$emit('updateCampaign', { send_at_local: null, send_now: true })
          const title = 'ad availability changed',
              content = 'the send date for the sponsored image is no longer available, please select a new message send date'
          this.setAlertModal(title, content)
        } else {
          this.$emit('setAvailableDates', availableDates)
        }
      },

      closePreviewModal() {
        this.previewAdId = null
        this.previewUrl = null
        this.suggestedText = null
        this.sendAsLink = this.adAsLink
        this.showPreviewModal = false
        this.attachedAd = null
      },

      async getAds() {
        this.loading = true
        try {
          const data = await merchantsAPI.getAds(this.campaignId)
          return this.onGetAdsSuccess(data)
        } catch (error) {
          this.onGetAdsError(error)
        } finally {
          this.loading = false
        }
      },

      onGetAdsError(error) {
        this.adsError = `issue updating the ads list. ${error}`
        this.skipAvailabilityCheck = false
      },

      onGetAdsSuccess(data) {
        const hasValidData = data?.length > 0 || this.recipientCount > 0,
              shouldCheckAvailability = hasValidData && (this.previewAdId || this.attachedAdId) && !this.skipAvailabilityCheck
        this.adsList = hasValidData ? this.sortFilterAdsList(data) : null
        this.adsError = null

        if (this.skipAvailabilityCheck) {
          // ads get sorted during the availability check, so sort them here and now.
          this.sortCurrentAdToTop()
        }

        // always reset skipAvailabilityCheck
        this.skipAvailabilityCheck = false

        shouldCheckAvailability && this.checkAdAvailability(!!this.previewAdId)
        return this.adsList;
      },

      removeAd(hasSuggestedText = false, ad) {
        const dataToUpdate = {
          ad_id: null,
          sms_sent: null,
          image_type: 'mms'
        }

        const selectedMessageTemplate = this.$store.getters.selectedNotificationTemplateFor('message')
        if (selectedMessageTemplate?.type === "brand") {
          dataToUpdate["template_id"] = null
        }

        this.sendAsLink = false
        this.previousAttachedAdId = this.attachedAdId
        this.attachedAd = null
        this.$emit('deselectedAd', ad);
        this.$emit('updateCampaign', dataToUpdate, null, null, null, hasSuggestedText)
        this.$emit('setAvailableDates', {})
      },

      // An ads credits_left will include campaigns currently bieng generated, so check
      // to see if the ad is attached to this campaign and ad back the current campaigns smsSent
      requiresLowerCount(ad) {
        const adCredits = ad?.credits_left,
          adIsSelected = ad?.id === this.attachedAdId,
          recipientRemaining = adIsSelected ? adCredits + this.smsSent : adCredits
        return recipientRemaining < this.recipientCount
      },

      setAlertModal(title, content) {
        this.modalConfig = {
          actionButton: 'continue',
          content: content,
          title: title,
          wrapperClasses: 'ad-alert-modal'
        }
        this.showAlertModal = true
      },

      setPageAlert() {
        const removeAlert = !this.attachedAdId,
            alert = {
              extraClasses: 'ads-alert',
              message: "your campaign is now completely free because one of your brands images has been attached - great work!"
            }
        this.$emit('updateAlerts', 'hasAttachedAd', removeAlert ? {} : alert)
      },

      selectedAd({ad, attachmentUrl, requestedFilters}) {
        this.previewAdId = ad.id
        this.previewUrl = attachmentUrl
        this.suggestedText = ad.suggested_text
        this.suggestedTextDisplay = this.urlify(ad.suggested_text)
        this.totalTextableMembers = ad?.total_textable_members
        this.requestedFilters = requestedFilters
        this.setPreviewModal()
        this.attachedAd = ad
      },

      setPreviewModal() {
        this.modalConfig = {
          actionButton: 'include this image',
          showClose: true,
          title: 'include this image?',
          wrapperClasses: 'image-preview-modal ad-preview-modal'
        }
        this.showPreviewModal = true
      },

      setUnavailableAlert() {
        const alert = {
              extraClasses: 'flash-alert',
              message: "unfortunately this image is no longer available, someone else must've picked it up since your last refresh",
              flashAlert: true,
              showClose: true,
        }
        this.$emit('updateAlerts', 'adUnavailable', alert)
      },

      sortFilterAdsList(data) {
        const adsWithCredits = [...data].filter(ad => ad.credits_left > 0),
          availableAds = adsWithCredits?.filter(ad => !this.requiresLowerCount(ad)) || [],
          unavailableAds = adsWithCredits?.filter(ad => this.requiresLowerCount(ad))?.sort((a, b) => b.credits_left - a.credits_left) || []
        return [...availableAds, ...unavailableAds]
      },

      sortCurrentAdToTop() {
        const currentAdIndex = this.adsList.map(ad => { return ad.id}).indexOf(this.attachedAdId),
            currentAdThumbnail = this.adsList.splice(currentAdIndex, 1)[0],
            adsListContainer = document.getElementById('branded-image-container')

        this.adsList.unshift(currentAdThumbnail)
        adsListContainer ? adsListContainer.scrollTop = 0 : null
      },

      updateSmsSent() {
        this.$emit('updateCampaign', {sms_sent: this.recipientCount})
      },

      uploadImage(isLink) {
        this.showPreviewModal = false
        this.sendAsLink = isLink
        this.getAds()
      },

      urlify(text) {
        if (!text) return null
        const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gi;
        return text.replace(urlRegex, function(url) {
          return '<a target="_blank" href="' + url + '">' + url + '</a>';
        })
      }
    },

    created() {
      this.sendAsLink = this.adAsLink
      this.getAds()
      if (!!this.attachedAdId) { this.removeAd() }
    },

    watch: {
      adAsLink: function() {
        this.sendAsLink = this.adAsLink
      },

      attachedAdId: function () {
        this.setPageAlert()
        !!this.attachedAdId && this.adsList ? this.sortCurrentAdToTop() : null

        // If there was an ad previously attached, get the ads again because
        // the credits_left would have changed due to the detach
        if (this.previousAttachedAdId) {
          // There is no need to check availability again since at this point the new ad was already attached
          this.skipAvailabilityCheck = true

          this.getAds()
        }
      },

      campaignSendTime: function () {
        this.getAds()
      },

      recipientCount: function () {
        this.getAds()
      },
    },

    computed: {
      imageRounding: function () {
        return this.currentMerchant.disclaimer ? 'top-round' : 'full-round'
      },

      overlayType: function () {
        if (!this.adsList) {
          return 1
        } else if (this.isEmailType) {
          return 3
        } else {
          return null
        }
      },

      ...mapState([
        'currentMerchant'
      ]),
    }
  }
</script>
